<script setup lang="ts">
import { Head, Link, router, useForm } from '@inertiajs/vue3'
import PageHeading from "@/Components/Headings/PageHeading.vue"
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Timeline from 'primevue/timeline'
import Fieldset from 'primevue/fieldset'
import { onMounted, ref, computed, inject, onUnmounted } from 'vue';
import { useFormatTime } from '@/Composables/useFormatTime';
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { useFormatNumber } from '@/Composables/useFormatNumber'
import Icon from '@/Components/Utils/Icon.vue'
import ModalConfirmationDelete from '@/Components/Utils/ModalConfirmationDelete.vue'
import Button from '@/Components/Utils/Button.vue'
import { notify } from '@kyvg/vue3-notification'
import Tag from '@/Components/Utils/Tag.vue'
import Chart from 'primevue/chart'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faSkull, faSeedling, faRocketLaunch, faCalendarAlt, faPlusCircle, faExclamationTriangle, faChevronLeft, faChevronRight } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
import Toggle from '@/Components/Pure/Toggle.vue'
import Modal from '@/Components/Utils/Modal.vue'
import { DatePicker, InputGroup, InputGroupAddon, InputNumber, Tabs, TabList, Tab, TabPanels, TabPanel } from 'primevue'
import { layoutStructure } from '@/Composables/useLayoutStructure'
library.add(faSkull, faSeedling, faRocketLaunch, faCalendarAlt, faPlusCircle, faExclamationTriangle, faChevronLeft, faChevronRight)

const props = defineProps<{
    pageHead?: PageHeadingTS
    title?: string
    product_data: {
        name: string
        code: string
        description: string
        type: string
        type_icon: {
            icon: string
            tooltip?: string
        }
        status_icon: {
            icon: string
            tooltip?: string
        }
        status: string
        is_pinned: boolean
        future_price?: number
        future_price_starts_on?: string
        is_able_include_tutup: boolean
        quantity: number
        price: number
        category?: {
            name: string
        }
        created_at: string
        slug: string
    }
    timeline: {}[]
    product_graph: {}
    customer_prices: {
        id: number
        customer_id: number
        price: number
        customer: {
            id: number
            name: string
            slug: string
        }
    }[]
    all_customers: {
        id: number
        name: string
        slug: string
    }[]
    order_history: {
        slug: string
        reference: string
        state: string
        state_label: string
        state_icon: { icon: string; color: string; tooltip?: string }
        quantity: number
        total_amount: number
        created_at: string
        customer: { name: string; slug: string } | null
        created_by: { name: string } | null
    }[]
    orders_count: number
    pinned_product: { id: number; name: string } | null
}>()

const layout = inject('layout', layoutStructure)

const setChartOptions = () => {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--p-text-color');
    const textColorSecondary = documentStyle.getPropertyValue('--p-text-muted-color');
    const surfaceBorder = documentStyle.getPropertyValue('--p-content-border-color');

    return {
        maintainAspectRatio: false,
        aspectRatio: 0.6,
        plugins: {
            legend: {
                labels: {
                    color: textColor
                }
            },
            tooltip: {
                callbacks: {
                    label: (context: any) => ` ${context.parsed.y} pcs dipesan`,
                    title: (items: any[]) => `Tanggal ${items[0].label}`,
                }
            }
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Tanggal',
                    color: textColorSecondary,
                },
                ticks: {
                    color: textColorSecondary
                },
                grid: {
                    color: surfaceBorder
                }
            },
            y: {
                title: {
                    display: true,
                    text: 'Jumlah dipesan (pcs)',
                    color: textColorSecondary,
                },
                ticks: {
                    color: textColorSecondary,
                    callback: (value: any) => `${value} pcs`,
                },
                grid: {
                    color: surfaceBorder
                },
                min: 0,
            }
        }
    };
}
const chartOptions = ref<typeof setChartOptions | null>(null)
const graphDate = ref(new Date())
const graphMonthLabel = computed(() => graphDate.value.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' }))
const isLoadingGraph = ref(false)

const navigateMonth = (delta: number) => {
    const d = new Date(graphDate.value)
    d.setMonth(d.getMonth() + delta)
    graphDate.value = d
    router.get(
        route('casheer.products.show', props.product_data.slug),
        { month: d.getMonth() + 1, year: d.getFullYear() },
        {
            only: ['product_graph'],
            preserveState: true,
            preserveScroll: true,
            onStart: () => { isLoadingGraph.value = true },
            onFinish: () => { isLoadingGraph.value = false },
        }
    )
}
onMounted(() => {
    if (props.product_data.type === 'seng') {
        layout.root_active = 'casheer.products.seng'
    }
    chartOptions.value = setChartOptions()
})

onUnmounted(() => {
    layout.root_active = ''
})

// Section: Toggle pinned product
const isLoadingToggle = ref(false)
const isPinConfirmOpen = ref(false)

const executePinPatch = (newVal: boolean) => {
    router.patch(
        route('casheer.products.update', props.product_data.id),
        { is_pinned: newVal, is_not_redirect: true },
        {
            preserveState: true,
            only: ['product_data'],
            preserveScroll: true,
            onStart: () => { isLoadingToggle.value = true },
            onFinish: () => { isLoadingToggle.value = false },
            onSuccess: () => {
                notify({
                    title: 'Berhasil mengubah!',
                    text: newVal ? 'Produk akan disematkan di Order.' : 'Produk tidak akan disematkan di Order.',
                    type: 'success',
                })
            },
            onError: () => {
                notify({ title: 'Gagal', text: 'Gagal mengubah status produk!', type: 'error' })
            },
        }
    )
}

const onChangeToggle = (newVal: boolean) => {
    if (newVal && props.pinned_product) {
        props.product_data.is_pinned = false
        isPinConfirmOpen.value = true
        return
    }
    executePinPatch(newVal)
}

const onConfirmPin = () => {
    isPinConfirmOpen.value = false
    props.product_data.is_pinned = true
    executePinPatch(true)
}

// Section: Toggle bolehkan order dengan tutup
const isLoadingOrderWithTutup = ref(false)
const onChangeToggleOrderWithTutup = (newVal: boolean) => {
    router.patch(
        route('casheer.products.update', props.product_data.id),
        {
            is_able_include_tutup: newVal,
            is_not_redirect: true
        },
        {
            preserveState: true,
            only: ['product_data'],
            preserveScroll: true,
            onStart: () => {
                isLoadingOrderWithTutup.value = true
            },
            onFinish: () => {
                isLoadingOrderWithTutup.value = false
            },
            onSuccess: () => {
                if (newVal) {
                    notify({
                        title: 'Berhasil mengubah!',
                        text: 'Produk diperbolehkan order dengan Tutup.',
                        type: 'success',
                    })
                } else {
                    notify({
                        title: 'Berhasil mengubah!',
                        text: 'Produk tidak diperbolehkan order dengan Tutup.',
                        type: 'success',
                    })
                }
                
            },
            onError: () => {
                notify({
                    title: 'Gagal',
                    text: 'Gagal mengubah boleh order dengan Tutup!',
                    type: 'error',
                })
            }
        }
    )
}

// Section: Schedule price
const isOpenSchedulePrice = ref(false)
const schedulePrice = useForm({
    future_price: props.product_data.future_price || props.product_data.price,
    future_price_starts_on: props.product_data.future_price_starts_on ? new Date(props.product_data.future_price_starts_on) : null,
    is_not_redirect: true
})
const onSubmitSchedulePrice = () => {
    const y = schedulePrice.future_price_starts_on.getFullYear()
    const m = String(schedulePrice.future_price_starts_on.getMonth() + 1).padStart(2, '0')
    const d = String(schedulePrice.future_price_starts_on.getDate()).padStart(2, '0')


    schedulePrice
        .transform((data) => ({
            ...data,
            future_price_starts_on: `${y}-${m}-${d}`,
        }))
        .patch(
        route('casheer.products.update', props.product_data.id),
        {
            preserveState: true,
            only: ['product_data'],
            preserveScroll: true,
            onSuccess: () => {
                notify({
                    title: 'Berhasil menjadwalkan harga baru!',
                    text: 'Harga baru akan berlaku sesuai jadwal.',
                    type: 'success',
                })
                isOpenSchedulePrice.value = false
                schedulePrice.defaults({
                    future_price: props.product_data.future_price || props.product_data.price,
                    future_price_starts_on: props.product_data.future_price_starts_on ? new Date(props.product_data.future_price_starts_on) : null,
                    is_not_redirect: true
                })
            },
            onError: () => {
                notify({
                    title: 'Gagal',
                    text: 'Gagal menjadwalkan harga baru!',
                    type: 'error',
                })
            }
        }
    )
}
const onResetScheduledPrice = () => {
    router.patch(
        route('casheer.products.update', props.product_data.id),
        {
            future_price: null,
            future_price_starts_on: null,
            is_not_redirect: true
        },
        {
            preserveState: true,
            only: ['product_data'],
            preserveScroll: true,
            onStart: () => {
                schedulePrice.processing = true
            },
            onSuccess: () => {
                notify({
                    title: 'Berhasil!',
                    text: 'Penjadwalan harga baru telah dibatalkan.',
                    type: 'success',
                })
                isOpenSchedulePrice.value = false
                schedulePrice.defaults({
                    future_price: props.product_data.price,
                    future_price_starts_on: null,
                    is_not_redirect: true
                })
            },
            onError: () => {
                notify({
                    title: 'Gagal!',
                    text: 'Gagal membatalkan penjadwalan harga baru.',
                    type: 'error',
                })
            },
            onFinish: () => {
                schedulePrice.processing = false
            }
        }
    )
}
let today = new Date();
let month = today.getMonth();
let year = today.getFullYear();
let prevMonth = (month === 0) ? 11 : month -1;
let prevYear = (prevMonth === 11) ? year - 1 : year;

const minDate = ref(new Date());
minDate.value.setMonth(prevMonth);
minDate.value.setFullYear(prevYear);

// Section: Customer special prices (Flexo only)
const isOpenAddCustomerPrice = ref(false)
const newCustomerPriceForm = useForm({
    customer_id: null as number | null,
    price: null as number | null,
})

const onSubmitCustomerPrice = () => {
    newCustomerPriceForm.post(
        route('casheer.products.customer_price.store', props.product_data.id),
        {
            preserveScroll: true,
            only: ['customer_prices'],
            onSuccess: () => {
                notify({ title: 'Berhasil!', text: 'Harga khusus berhasil disimpan.', type: 'success' })
                isOpenAddCustomerPrice.value = false
                newCustomerPriceForm.reset()
            },
            onError: () => {
                notify({ title: 'Gagal', text: 'Gagal menyimpan harga khusus.', type: 'error' })
            },
        }
    )
}

const pendingDeleteCustomerId = ref<number | null>(null)
const isDeletingCustomerPrice = ref(false)

const onDeleteCustomerPrice = (customerId: number) => {
    pendingDeleteCustomerId.value = customerId
}

const onConfirmDeleteCustomerPrice = () => {
    if (!pendingDeleteCustomerId.value) return
    isDeletingCustomerPrice.value = true
    router.delete(
        route('casheer.products.customer_price.delete', props.product_data.id),
        {
            data: { customer_id: pendingDeleteCustomerId.value },
            preserveScroll: true,
            only: ['customer_prices'],
            onSuccess: () => {
                notify({ title: 'Berhasil!', text: 'Harga khusus berhasil dihapus.', type: 'success' })
                pendingDeleteCustomerId.value = null
            },
            onError: () => {
                notify({ title: 'Gagal', text: 'Gagal menghapus harga khusus.', type: 'error' })
            },
            onFinish: () => {
                isDeletingCustomerPrice.value = false
            },
        }
    )
}

const availableCustomers = computed(() => {
    const existingIds = new Set(props.customer_prices.map(cp => cp.customer_id))
    return props.all_customers?.filter(c => !existingIds.has(c.id))
})
</script>

<template>

    <Head :title="title" />
    <PageHeading :data="pageHead">
        <!-- Button: delete -->
        <template #button-delete-product="{ action }">
            <div>
                <ModalConfirmationDelete
                    :routeDelete="action.route"
                    title="Yakin ingin menghapus produk?"
                    description="Jika ada data (order/manufacture/dll) yang tertaut dengan produk ini, maka produk ini hanya akan dinon-aktifkan."
                    isFullLoading
                    @success="() => {
                        notify({
                            title: 'Berhasil',
                            text: 'Produk berhasil terhapus!',
                            type: 'success',
                        })
                    }"
                >
                    <template #default="{ isOpenModal, changeModel }">
                        <Button
                            @click="() => changeModel()"
                            :style="action.style"
                            :label="action.label"
                            :icon="action.icon"
                            :iconRight="action.iconRight"
                            :key="`ActionButton${action.label}${action.style}`"
                            :tooltip="action.tooltip"
                        />
                    </template>
                </ModalConfirmationDelete>
            </div>
        </template>

        <template #afterTitle>
            <Tag v-if="product_data.status === 'inactive'" :theme="7" noHoverColor>
                <template #label>
                    <Icon :data="product_data.status_icon" />
                    {{ product_data.status_icon?.tooltip }}
                </template>
                <!-- {{ product_data.state }} -->
            </Tag>
        </template>
    </PageHeading>



    <div class="px-4 sm:px-6 lg:px-8 ">
        <!-- Section: shortcut menu -->
        <!-- <div class="py-1 mt-2">
            <div @click="() => isOpenSchedulePrice = true" class="underline text-gray-500 hover:text-gray-700 w-fit cursor-pointer">
                <FontAwesomeIcon icon='fal fa-calendar-alt' class='mr-1' fixed-width aria-hidden='true' />
                Jadwalkan harga baru
            </div>
        </div> -->

        <!-- Section: Warning -->
        <!-- <div v-if="props.product_data.future_price && props.product_data.future_price_starts_on" class="bg-yellow-100 text-yellow-700 border border-yellow-500 w-full rounded px-4 py-2 mt-4 mx-auto">
            <FontAwesomeIcon icon='fas fa-exclamation-triangle' class='' fixed-width aria-hidden='true' />
            Produk akan berubah harga dari <span class="font-bold">{{ useFormatCurrency(props.product_data.price) }}</span> ke <span class="font-bold">{{ useFormatCurrency(props.product_data.future_price) }}</span> pada <span class="xfont-semibold">{{ useFormatTime(props.product_data.future_price_starts_on) }}</span>.
            <span @click="isOpenSchedulePrice = true" class="underline cursor-pointer opacity-80 hover:opacity-100">Klik disini untuk mengubah</span>
        </div> -->

        <!-- Section: Whole data -->
        <div class="mt-6 grid md:grid-cols-2 gap-x-4 gap-y-6 w-full ">
            <div class="md:col-span-2">
                <div class="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div class="border-l-4 text-xl border-sky-400 pl-2 pr-4 bg-sky-50 w-fit py-1">
                        Grafik orderan produk perbulan
                        <FontAwesomeIcon v-tooltip="'Jumlah pcs dari Order yang masuk'" icon='fal fa-info-circle' class='text-gray-400' fixed-width aria-hidden='true' />
                    </div>
                    <div class="flex items-center gap-2">
                        <button @click="navigateMonth(-1)" :disabled="isLoadingGraph" class="p-1.5 rounded hover:bg-gray-100 transition disabled:opacity-50">
                            <FontAwesomeIcon icon='fal fa-chevron-left' fixed-width aria-hidden='true' />
                        </button>
                        <span class="capitalize text-sm font-medium w-36 text-center">{{ graphMonthLabel }}</span>
                        <button @click="navigateMonth(1)" :disabled="isLoadingGraph" class="p-1.5 rounded hover:bg-gray-100 transition disabled:opacity-50">
                            <FontAwesomeIcon icon='fal fa-chevron-right' fixed-width aria-hidden='true' />
                        </button>
                    </div>
                </div>
                <div v-if="isLoadingGraph" class="h-[20rem] skeleton w-full"></div>
                <Chart v-else-if="chartOptions" type="line" :data="product_graph" :options="chartOptions" class="h-[20rem]" />
                <div v-else class="h-[20rem] skeleton w-full"></div>
            </div>
            <div class="max-w-xl mt-6 grid grid-cols-1 gap-x-6 gap-y-8 xl:gap-x-8 h-fit ">
                <div class="w-full overflow-hidden rounded-xl border border-sky-400">
                    <div class="flex justify-between items-center gap-x-4 border-b border-gray-900/5 bg-gray-50 p-6">
                        <div>
                            <div class="font-bold">
                                <Icon :data="product_data.type_icon" />
                                {{ product_data.name }}
                            </div>
                            <div class="text-sm/6 text-gray-500">{{ product_data.description }}</div>
                            <div v-tooltip="'Tgl. dibuat'" class="text-sm/6 text-gray-500 italic">{{ useFormatTime(product_data.created_at) }}</div>
                        </div>
        
                        <div class="flex flex-col items-end text-right gap-y-1">
                            <div class="text-sm text-gray-400">Sematkan?</div>
                            <div class="flex items-center gap-x-1">
                                <FontAwesomeIcon v-tooltip="'Jika iya, maka akan ditampilkan di posisi teratas dari daftar produk ketika membuat Order baru'" icon='fal fa-info-circle' class='cursor-pointer text-gray-400' fixed-width aria-hidden='true' />
                                <Toggle
                                    v-model="product_data.is_pinned"
                                    @update:model-value="(value) => onChangeToggle(value)"
                                    :disabled="isLoadingToggle"
                                    :isLoading="isLoadingToggle"
                                />
                            </div>
                        </div>
                    </div>
        
                    <dl class="-my-3 divide-y divide-gray-100 px-6 py-4 text-sm/6">
                        <div class="flex justify-between gap-x-4 py-3">
                            <dt class="text-gray-500">Kode</dt>
                            <dd class="flex items-start gap-x-2">
                                <div class="font-medium">{{ product_data.code || '-' }}</div>
                            </dd>
                        </div>
                        <div class="flex justify-between gap-x-4 py-3">
                            <dt class="text-gray-500">Harga satuan</dt>
                            <dd class="flex items-start gap-x-2">
                                <div class="font-medium">{{ useFormatCurrency(product_data.price || 0) }}</div>
                            </dd>
                        </div>
                        <div v-if="product_data.type != 'custom'" class="flex justify-between gap-x-4 py-3">
                            <dt class="text-gray-500">Stok saat ini</dt>
                            <dd class="flex items-start gap-x-2">
                                <div class="font-medium">{{ useFormatNumber(product_data.quantity || 0) }} pcs</div>
                                <!-- <div :class="'rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset'">
                                    rrr
                                </div> -->
                            </dd>
                        </div>
        
                        <!-- Row: Bolehkan order dengan tutup -->
                        <div v-if="product_data.type == 'custom'" class="flex justify-between gap-x-4 py-3">
                            <dt class="text-gray-500">
                                Bolehkan order dengan Tutup?
                                <FontAwesomeIcon v-tooltip="'Jika diaktifkan, maka sales bisa memilih untuk include produk Tutup ketika membuat order'" icon='fal fa-info-circle' class='cursor-pointer text-gray-400' fixed-width aria-hidden='true' />
                            </dt>
                            <dd class="flex items-start gap-x-2">
                                <Toggle
                                    v-model="product_data.is_able_include_tutup"
                                    @update:model-value="(value) => onChangeToggleOrderWithTutup(value)"
                                    :disabled="isLoadingOrderWithTutup"
                                    :isLoading="isLoadingOrderWithTutup"
                                />
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>
            <div class="py-1 justify-self-end flex justify-end w-full md:w-96">
                <Fieldset v-if="product_data.type !== 'custom'" legend="Product History" class="h-fit w-full text-red-500">
                    <Timeline v-if="timeline.length" :value="timeline">
                        <template #marker="slotProps">
                            <span class="bg-gray-400 flex w-12 h-12 items-center justify-center text-xs rounded-full z-10 shadow-sm whinowr"
                                :class="slotProps.item.quantity_change < 0 ? 'bg-red-50 border border-red-300 text-red-600' : 'bg-green-50 border border-green-300 text-green-600'"
                            >
                                {{ useFormatNumber(slotProps.item.quantity_change) }}
                                <!-- <FontAwesomeIcon icon='fal fa-shredder' class='' fixed-width aria-hidden='true' /> -->
                            </span>
                        </template>
                        <template #opposite="slotProps">
                            <div class="grid">
                                <small v-tooltip="useFormatTime(slotProps.item.created_at, {formatTime: 'hms' })" class="whitespace-nowrap">{{ useFormatTime(slotProps.item.created_at)}}</small>
                                <div class="text-gray-400 italic text-sm capitalize">
                                    <div v-if="slotProps.item?.order_id">
                                        <Link v-if="slotProps.item?.order?.slug" :href="slotProps.item?.order?.slug ? route('casheer.orders.show', slotProps.item?.order?.slug) : '#'" class="font-medium text-gray-500 hover:underline">
                                            {{ slotProps.item?.order?.reference }}
                                        </Link>
                                        <br>
                                        <div v-if="slotProps.item.type" class="text-[0.8em]">{{ slotProps.item.type }}</div>
                                    </div>
                                    <div v-else-if="slotProps.item?.manufacture_id">
                                        <Link :href="route('manufacture.show', slotProps.item?.manufacture?.slug)" class="font-medium text-gray-500 hover:underline">
                                            {{ slotProps.item.type }}
                                        </Link>
                                        <br>
                                        <div class="text-[0.8em]">({{ slotProps.item.manufacture?.creator?.name || '-' }})</div>
                                    </div>
                                    <span v-else>
                                        {{ slotProps.item.type }}
                                    </span>
                                </div>
                            </div>
                        </template>
        
                        <template #content="slotProps">
                            <div class="grid mt-0.5">
                                <p class="font-bold">{{ useFormatNumber(slotProps.item.quantity_end) }} <span class="font-normal">pcs</span></p>
                                <br>
                                <p v-if="slotProps.item?.description" class="text-xs italic text-gray-400">
                                    {{ slotProps.item?.description }}
                                </p>
                            </div>
                        </template>
                    </Timeline>
                    <div v-else class="text-gray-400 text-center">
                        Belum ada riwayat perubahan stok
                    </div>
                </Fieldset>
            </div>
            <!-- Section: Customer Special Prices (Flexo only) -->
        <div v-if="product_data.type === 'flexo'" class="md:col-span-2 mt-6">
            <div class="border-l-4 border-blue-400 pl-2 bg-blue-50 w-fit py-1 text-xl mb-4">
                Harga Khusus per Pelanggan
            </div>

            <div class="overflow-x-auto rounded-xl border border-gray-200">
                <table class="min-w-full divide-y divide-gray-200 text-sm">
                    <thead class="bg-gray-50">
                        <tr>
                            <th class="px-4 py-3 text-left font-semibold text-gray-600">Pelanggan</th>
                            <th class="px-4 py-3 text-right font-semibold text-gray-600">Harga Khusus</th>
                            <th class="px-4 py-3 text-right font-semibold text-gray-600">vs. Harga Umum</th>
                            <th class="px-4 py-3 text-center font-semibold text-gray-600">Aksi</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100 bg-white">
                        <tr v-for="cp in customer_prices" :key="cp.id" class="hover:bg-gray-50">
                            <td class="px-4 py-3">
                                <Link :href="route('casheer.customers.show', cp.customer.slug)" class="primaryLink font-medium">
                                    {{ cp.customer.name }}
                                </Link>
                            </td>
                            <td class="px-4 py-3 text-right font-medium text-blue-600">
                                {{ useFormatCurrency(cp.price) }}
                            </td>
                            <td class="px-4 py-3 text-right text-sm">
                                <span v-if="cp.price < product_data.price" class="text-green-600">
                                    -{{ useFormatCurrency(product_data.price - cp.price) }}
                                </span>
                                <span v-else-if="cp.price > product_data.price" class="text-red-500">
                                    +{{ useFormatCurrency(cp.price - product_data.price) }}
                                </span>
                                <span v-else class="text-gray-400 italic">Sama</span>
                            </td>
                            <td class="px-4 py-3 text-center">
                                <button
                                    @click="onDeleteCustomerPrice(cp.customer_id)"
                                    class="text-red-500 hover:text-red-700 text-xs underline"
                                >
                                    Hapus
                                </button>
                            </td>
                        </tr>
                        <tr v-if="!customer_prices.length">
                            <td colspan="4" class="px-4 py-6 text-center text-gray-400 italic">
                                Belum ada pelanggan dengan harga khusus untuk produk ini
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <button
                @click="isOpenAddCustomerPrice = true"
                class="mt-3 flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 underline"
            >
                <FontAwesomeIcon icon="fal fa-plus-circle" fixed-width aria-hidden="true" />
                Tambah Harga Khusus Pelanggan
            </button>
        </div>
    <!-- <pre>{{ timeline }}</pre> -->
    </div>
</div>

        <!-- Section: Order History -->
        <div class="px-4 sm:px-6 lg:px-8 mt-8 mb-10">
            <div class="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div class="border-l-4 border-amber-400 pl-2 bg-amber-50 w-fit py-1 text-xl">
                    Riwayat Order
                    <span class="text-sm font-normal text-gray-500 ml-1">({{ orders_count }} total)</span>
                </div>
                <Link
                    v-if="orders_count > 5"
                    :href="route('layout.orders.index', { 'filter[global]': product_data.name })"
                    class="text-sm text-amber-600 hover:text-amber-800 underline"
                >
                    Lihat semua {{ orders_count }} order →
                </Link>
            </div>
            <div class="overflow-x-auto rounded-xl border border-gray-200">
                <table class="min-w-full divide-y divide-gray-200 text-sm">
                    <thead class="bg-gray-50">
                        <tr>
                            <th class="px-4 py-3 text-left font-semibold text-gray-600">No. Order</th>
                            <th class="px-4 py-3 text-left font-semibold text-gray-600">Pelanggan</th>
                            <th class="px-4 py-3 text-left font-semibold text-gray-600">Dibuat oleh</th>
                            <th class="px-4 py-3 text-center font-semibold text-gray-600">Jumlah</th>
                            <th class="px-4 py-3 text-right font-semibold text-gray-600">Total</th>
                            <th class="px-4 py-3 text-left font-semibold text-gray-600">Status</th>
                            <th class="px-4 py-3 text-left font-semibold text-gray-600">Tgl. Order</th>
                        </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-100 bg-white">
                        <tr v-for="order in order_history" :key="order.slug" class="hover:bg-gray-50">
                            <td class="px-4 py-3">
                                <Link :href="route('casheer.orders.show', order.slug)" class="primaryLink font-medium">
                                    {{ order.reference }}
                                </Link>
                            </td>
                            <td class="px-4 py-3">
                                <Link v-if="order.customer" :href="route('casheer.customers.show', order.customer.slug)" class="primaryLink">
                                    {{ order.customer.name }}
                                </Link>
                                <span v-else class="text-gray-400 italic">-</span>
                            </td>
                            <td class="px-4 py-3 text-gray-600">
                                {{ order.created_by?.name || '-' }}
                            </td>
                            <td class="px-4 py-3 text-center">
                                {{ order.quantity }} pcs
                            </td>
                            <td class="px-4 py-3 text-right font-medium">
                                {{ useFormatCurrency(order.total_amount) }}
                            </td>
                            <td class="px-4 py-3">
                                <Icon :data="order.state_icon" />
                                <span class="ml-1 text-gray-600">{{ order.state_label }}</span>
                            </td>
                            <td class="px-4 py-3 text-gray-500 whitespace-nowrap">
                                {{ useFormatTime(order.created_at) }}
                            </td>
                        </tr>
                        <tr v-if="!order_history.length">
                            <td colspan="7" class="px-4 py-6 text-center text-gray-400 italic">
                                Belum ada riwayat order untuk produk ini
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <!-- <div v-if="orders_count > 5" class="mt-3 text-right">
                <Link
                    :href="route('layout.orders.index', { 'filter[global]': product_data.name })"
                    class="text-sm text-amber-600 hover:text-amber-800 underline"
                >
                    Lihat semua {{ orders_count }} order →
                </Link>
            </div> -->
        </div>

    <Modal v-if="false" :isOpen="isOpenSchedulePrice" @onClose="() => (isOpenSchedulePrice = false)" width="w-full max-w-xl" closeButton>
		<div class="bg-white px-6 py-6 sm:rounded-lg sm:px-12">
            <div class="text-center text-xl font-medium">
                Jadwalkan Harga Baru
            </div>

            <div class="space-y-6 mt-6">
                <!-- Field: Harga lama -->
                <div>
                    <label for="old_price" class="block text-sm/6 font-medium ">Harga lama</label>
                    <InputGroup class="mt-1">
                        <InputGroupAddon>Rp</InputGroupAddon>
                        <InputNumber :model-value="product_data.price" placeholder="Harga lama" disabled />
                    </InputGroup>
                </div>

                <!-- Field: Harga baru -->
                <div>
                    <label for="new_price" class="block text-sm/6 font-medium ">Harga baru<span class="text-base text-red-500 italic">*</span></label>
                    <InputGroup class="mt-1">
                        <InputGroupAddon>Rp</InputGroupAddon>
                        <InputNumber
                            :modelValue="schedulePrice.future_price"
                            @input="(val) => (console.log('vavaval', val.value), schedulePrice.future_price = val.value)"
                            placeholder="Harga baru"
                            required
                        />
                    </InputGroup>
                    <div v-if="schedulePrice.errors.future_price" class="text-sm text-red-500 italic">
                        *{{ schedulePrice.errors.future_price }}
                    </div>
                </div>

                <!-- Field: berlaku mulai -->
                <div class="!mb-10">
                    <label for="future_price_starts_on" class="block text-sm/6 font-medium ">Berlaku mulai<span class="text-base text-red-500 italic">*</span></label>
                    <div class="mt-1">
                        <DatePicker
                            v-model="schedulePrice.future_price_starts_on"
                            placeholder="Pilih tanggal"
                            dateFormat="DD, dd M yy"
                            fluid
                            required
                            showIcon
                            iconDisplay="input"
                            xminDate="(() => { const d = new Date(); d.setDate(d.getDate() + 1); return d })()"
                        />
                    </div>
                    <div v-if="schedulePrice.errors.future_price_starts_on" class="text-sm text-red-500 italic">
                        *{{ schedulePrice.errors.future_price_starts_on }}
                    </div>
                </div>

                <!-- Button: simpan -->
                <div>
                    <Button
                        label="Simpan"
                        type="save"
                        @click="onSubmitSchedulePrice"
                        full
                        v-tooltip="!schedulePrice.isDirty ? 'Tidak ada perubahan' : ''"
                        :disabled="!schedulePrice.isDirty || !schedulePrice.future_price || !schedulePrice.future_price_starts_on"
                        :loading="schedulePrice.processing"
                    />

                    <Button
                        v-if="product_data.future_price && product_data.future_price_starts_on"
                        class="mt-4"
                        label="Hapus penjadwalan harga"

                        @click="onResetScheduledPrice"
                        full
                        type="delete"
                        :loading="schedulePrice.processing"
                    />
                </div>
            </div>

            
        </div>
	</Modal>

    <!-- Modal: Tambah Harga Khusus Pelanggan -->
    <Modal :isOpen="isOpenAddCustomerPrice" @onClose="() => { isOpenAddCustomerPrice = false; newCustomerPriceForm.reset() }" width="w-full max-w-lg" closeButton>
        <div class="bg-white px-6 py-6 sm:rounded-lg">
            <div class="text-center text-xl font-medium mb-6">Tambah Harga Khusus</div>

            <div class="space-y-5">
                <!-- Field: Pelanggan -->
                <div>
                    <label class="block text-sm/6 font-medium mb-1">
                        Pelanggan <span class="text-red-500">*</span>
                    </label>
                    <select
                        v-model="newCustomerPriceForm.customer_id"
                        class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                        <option :value="null" disabled>Pilih pelanggan...</option>
                        <option v-for="c in availableCustomers" :key="c.id" :value="c.id">
                            {{ c.name }}
                        </option>
                    </select>
                    <p v-if="availableCustomers?.length === 0" class="text-xs text-gray-400 mt-1 italic">
                        Semua pelanggan sudah memiliki harga khusus untuk produk ini.
                    </p>
                    <div v-if="newCustomerPriceForm.errors.customer_id" class="text-sm text-red-500 mt-1">
                        {{ newCustomerPriceForm.errors.customer_id }}
                    </div>
                </div>

                <!-- Field: Harga khusus -->
                <div>
                    <label class="block text-sm/6 font-medium mb-1">
                        Harga Khusus <span class="text-red-500">*</span>
                    </label>
                    <InputGroup>
                        <InputGroupAddon>Rp</InputGroupAddon>
                        <InputNumber
                            :modelValue="newCustomerPriceForm.price"
                            @input="(val) => (newCustomerPriceForm.price = val.value)"
                            placeholder="Contoh: 25000"
                        />
                    </InputGroup>
                    <p class="text-xs text-gray-400 mt-1">Harga umum: {{ useFormatCurrency(product_data.price) }}</p>
                    <div v-if="newCustomerPriceForm.errors.price" class="text-sm text-red-500 mt-1">
                        {{ newCustomerPriceForm.errors.price }}
                    </div>
                </div>

                <Button
                    label="Simpan"
                    type="save"
                    full
                    :disabled="!newCustomerPriceForm.customer_id || !newCustomerPriceForm.price"
                    :loading="newCustomerPriceForm.processing"
                    @click="onSubmitCustomerPrice"
                />
            </div>
        </div>
    </Modal>

    <!-- Modal: Konfirmasi hapus harga khusus -->
    <Modal :isOpen="!!pendingDeleteCustomerId" @onClose="() => pendingDeleteCustomerId = null" width="w-full max-w-md" closeButton>
        <div class="bg-white xpx-6 xpy-6 sm:rounded-lg">
            <div class="text-center mb-4">
                <FontAwesomeIcon icon="fal fa-exclamation-triangle" class="text-amber-400 text-4xl" fixed-width aria-hidden="true" />
            </div>
            <div class="text-center text-xl font-medium mb-2">Hapus Harga Khusus?</div>
            <p class="text-center text-sm text-gray-500 mb-6">
                Harga khusus customer {{ 'nama customer' }} ini akan dihapus.<br>
                <span class="font-medium text-gray-700">Order yang sudah ada tidak akan terdampak</span>, hanya order yang baru dibuat setelah ini yang akan menggunakan harga umum produk.
            </p>
            <div class="flex gap-3">
                <Button
                    label="Batal"
                    type="tertiary"
                    :disabled="isDeletingCustomerPrice"
                    @click="() => pendingDeleteCustomerId = null"
                />
                <Button
                    label="Ya, hapus harga"
                    type="red"
                    full
                    :loading="isDeletingCustomerPrice"
                    @click="onConfirmDeleteCustomerPrice"
                />
            </div>
        </div>
    </Modal>

    <!-- Modal: Konfirmasi ganti sematan produk -->
    <Modal :isOpen="isPinConfirmOpen" @onClose="() => isPinConfirmOpen = false" width="w-full max-w-md" closeButton>
        <div class="bg-white px-6 py-6 sm:rounded-lg">
            <div class="text-center mb-4">
                <FontAwesomeIcon icon="fal fa-exclamation-triangle" class="text-amber-400 text-4xl" fixed-width aria-hidden="true" />
            </div>
            <div class="text-center text-xl font-medium mb-2">Ganti Produk Sematkan?</div>
            <p class="text-center text-sm text-gray-500 mb-6">
                Produk <span class="font-semibold text-gray-800">{{ pinned_product?.name }}</span> saat ini sudah disematkan.<br>
                Melanjutkan akan melepas sematannya dan menggantinya dengan produk ini.
            </p>
            <div class="flex gap-3">
                <Button
                    label="Batal"
                    type="cancel"
                    full
                    @click="() => isPinConfirmOpen = false"
                />
                <Button
                    label="Ya, Ganti"
                    type="save"
                    full
                    :loading="isLoadingToggle"
                    @click="onConfirmPin"
                />
            </div>
        </div>
    </Modal>
</template>